<template>
    <div :class="classes">
        <fieldset :class="theme.material">
            <textarea
                v-if="multiline"
                @input="handleInput($event.target.value)"
                @focus="handleFocus" @blur="handleBlur"
                :value="value" maxlength="1000"
                :name="name"
                :class="theme.inputRaw"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly">
            </textarea>
            <div v-if="multiline" v-html="valueHtml" :class="theme.multilineHtml"></div>
    
            <input
                v-if="!multiline && !mask"
                @input="handleInput($event.target.value)"
                @keypress="handleValidate"
                @focus="handleFocus"
                @blur="handleBlur"
                :autocomplete="autocomplete"
                :value="value"
                :maxlength="maxlength"
                :type="inputType"
                :name="name"
                :class="theme.inputRaw"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
            />
            
            <the-mask
                v-if="!multiline && mask"
                @input="handleInput"
                @focus.native="handleFocus"
                @blur.native="handleBlur"
                :autocomplete="autocomplete"
                :maxlength="maxlength"
                :value="value"
                :name="name"
                :mask="this.inputMask"
                :tokens="this.inputTokens"
                masked
                :class="theme.inputRaw"
                :placeholder="inputPlaceholder"
                :readonly="readonly"
                :disabled="disabled"
            />
    
            <hr :class="theme.underline" />
            <label :class="theme.label">{{ label }}</label>

            <div
                @mouseleave="handleMouseLeave"
                @mouseenter="handleMouseEnter"
                :class="theme.absoluteRight"
            >
                <slot name="assistant-action"></slot>
            </div>
        </fieldset>

        <div :class="theme.relative">
            <div
                @mouseleave="handleMouseLeave"
                @mouseenter="handleMouseEnter"
                :class="[theme.absoluteRight, theme.isUp]"
            >
                <transition
                    name="fadeDown"
                    :enter-active-class="theme.fadeDownEnterActive"
                    :enter-class="theme.fadeDownEnter"
                    :enter-to-class="theme.fadeDownEnterTo"
                    :leave-active-class="theme.fadeDownLeaveActive"
                    :leave-class="theme.fadeDownLeave"
                    :leave-to-class="theme.fadeDownLeaveTo"
                    @before-leave="$emit('assistant-before-leave')"
                    @after-leave="$emit('assistant-after-leave')"
                >
                    <slot name="assistant"></slot>
                </transition>
            </div>
        </div>

        <slot></slot>

        <div :class="[theme.caption, theme.hasError]"><slot name="error" :error="error">{{error}}</slot></div>

        <div :class="theme.caption"><slot name="caption" :caption="caption">{{caption}}</slot></div>
    </div>
</template>
<script>
    import { TheMask } from 'vue-the-mask';
    import theme from './inputText.scss';

    function validateValue(type, e) {
        const val = e.key;

        if (val === undefined || e.ctrlKey || e.altKey || e.metaKey) {
            return true;
        }

        switch (val.toLowerCase()) {
            case 'backspace':
            case 'enter':
            case 'tab':
            case 'arrowleft':
            case 'arrowtop':
            case 'arrowright':
            case 'arrowbottom':
                return true;
        }

        switch (type) {
            case 'email':
                return val.toString().match(/\s|[а-яё]+/gi) === null;
            case 'number':
                return val.toString().length === 0 ||
                    val.toString().match(/^\d+$/) !== null;
            default:
                return true;
        }
    }

    export default {
        name: 'input-text',
        props: {
            multiline: {
                type: Boolean,
                default: false,
            },
            value: {
                type: [String, Number],
                default: ''
            },
            type: {
                type: String,
                default: 'text'
            },
            label: String,
            placeholder: String,
            disabled: Boolean,
            readonly: Boolean,
            caption: String,
            mask: String,
            autocomplete: String,
            error: String,
            name: String,
            maxlength: {
                type: [String, Number],
                default() {
                    return 100;
                },
            },
            theme: {
                type: Object,
                default() {
                    return theme;
                }
            }
        },
        components: { TheMask },
        data() {
            return {
                isFocused: false,
                isOverAssistance: false,
            }
        },
        created() {
            window.addEventListener('click', this.handleClickOutside);
        },
        destroyed() {
            window.removeEventListener('click', this.handleClickOutside);
        },
        computed: {
            valueHtml() {
                return this.value.replace(/(?:\r\n|\r|\n)/g, '<br/>') + '<br/>';
            },
            isHasAssistantActionSlot() {
                return ['$scopedSlots', '$slots']
                    .some((slot) => this[slot].hasOwnProperty('assistant-action'))
            },
            classes() {
                return {
                    [this.theme.input]: true,
                    [this.theme.hasAssistantActionSlot]: this.isHasAssistantActionSlot,
                    [this.theme.isEntered]: this.value.toString().trim().length > 0,
                    [this.theme.isFocused]: this.isFocused,
                    [this.theme.isDisabled]: this.disabled,
                    [this.theme.isReadonly]: this.readonly,
                    [this.theme.isOutline]: true,
                    [this.theme.hasError]: this.error && this.error.length,
                    [this.theme.isMultiline]: this.multiline,
                }
            },
            inputType() {
                switch(this.type) {
                    case 'password':
                    case 'email':
                        return this.type;
                    default:
                        return 'text';
                }
            },
            inputMask() {
                switch (this.mask) {
                    case 'date':
                        return '##/##/####';
                    case 'dateWithoutDay':
                        return '##/####';
                    case 'phone':
                        return '+7 (###) ###-##-##';
                    case 'code':
                        return '# # # #';
                    case 'driverLicense':
                        return '##FF ######';
                    case 'vehicleLicensePlate':
                        return 'V###VV###'
                    case 'vehicleRegistrationCertificate':
                        return 'FF FF ######'
                    case 'vin':
                        return 'XXXXXXXXXXXXXXXXX'
                    case 'issuingAuthorityDepartment':
                        return '###-###'
                    default:
                        return '';
                }
            },
            inputPlaceholder() {
                switch (this.mask) {
                    case 'date':
                        return '';
                    case 'phone':
                        return '+7 (___) ___-__-__';
                    case 'code':
                        return '_ _ _ _';
                    case 'driverLicense':
                        return '__ __ ____';
                    case 'vehicleLicensePlate':
                        return '_________'
                    case 'vehicleRegistrationCertificate':
                        return '__ __ ______'
                    default:
                        return this.placeholder;
                }
            },
            inputTokens() {
                return {
                    'K': {
                        pattern: /[a-zA-ZА-Яа-я]/,
                        transform: v => v.toLocaleUpperCase()
                    },
                    'F': {
                        pattern: /[a-zA-Z0-9А-Яа-я]/,
                        transform: v => v.toLocaleUpperCase()
                    },
                    'C': {
                        pattern: /[А-Яа-я0-9]/,
                        transform: v => v.toLocaleUpperCase()
                    },
                    'V': {
                        pattern: /[АВЕКМНОРСТУХавекмнорстух0-9]/,
                        transform: v => v.toLocaleUpperCase()
                    },
                    '#': {pattern: /\d/},
                    'X': {pattern: /[0-9a-zA-Z]/},
                    'S': {pattern: /[a-zA-Z]/},
                    'A': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleUpperCase()},
                    'a': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleLowerCase()},
                    '!': {escape: true}
                };
            },
            isCanChange() {
                return !(this.readonly || this.disabled);
            }
        },
        methods: {
            handleFocus() {
                this.isFocused = true;
                this.$emit('focus');
            },
            handleBlur() {
                setTimeout(() => {
                    this.isFocused = false;
                }, 150);
                this.$emit('blur');
            },
            handleChange(value) {
                this.$emit('change', value);
                this.$emit('input', value);
            },
            handleInput(value) {
                this.handleChange(value);
            },
            handleValidate(e) {
                if (!validateValue(this.type, e)) {
                    e.preventDefault();
                    return false;
                }
            },
            handleMouseLeave() {
                this.isOverAssistance = false;
            },
            handleMouseEnter() {
                this.isOverAssistance = true;
            },
            handleClickOutside() {
                if (!this.isOverAssistance) {
                    this.$emit('assistant-outside-click');
                }
            }
        },
    }
</script>


// WEBPACK FOOTER //
// InputText.vue?479667eb